import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, 
  Film, 
  ShieldCheck, 
  ArrowRight,
  CheckCircle,
  Users,
  BarChart3,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export const Solutions = ({ onDemoClick }) => {
  const solutions = [
    {
      icon: GraduationCap,
      title: 'Plataforma EAD',
      subtitle: 'Faculdade Corporativa',
      description: 'Plataforma completa de ensino a distância personalizada com sua marca, domínio próprio e todas as funcionalidades para gerenciar a educação corporativa.',
      features: [
        'Domínio personalizado (universidade.suaempresa.com.br)',
        'Trilhas de aprendizagem e turmas',
        'Certificados automáticos personalizados',
        'Gamificação e engajamento',
        'Relatórios e dashboards completos',
        'Integração com sistemas de RH',
      ],
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=500&fit=crop',
      path: '/solucoes/plataforma-ead',
      color: 'blue',
    },
    {
      icon: Film,
      title: 'Produção de Cursos',
      subtitle: 'Estúdio + Edição',
      description: 'Criação de conteúdo educacional com produção profissional. Do roteiro à publicação, cuidamos de todo o processo com qualidade cinematográfica.',
      features: [
        'Estúdio profissional equipado',
        'Roteirização e storytelling',
        'Gravação em alta definição',
        'Edição e motion graphics',
        'Design instrucional especializado',
        'Formatos diversos (vídeo, podcast, interativo)',
      ],
      image: 'https://images.unsplash.com/photo-1769699167679-a3184af4d8f1?w=800&h=500&fit=crop',
      path: '/solucoes/producao-cursos',
      color: 'orange',
    },
    {
      icon: ShieldCheck,
      title: 'NR-01 Psicossocial',
      subtitle: 'Conformidade e Gestão',
      description: 'Ferramenta de apoio para organização e gestão dos requisitos de saúde mental no trabalho conforme a NR-01, facilitando o processo de conformidade.',
      features: [
        'Questionários padronizados',
        'Relatórios de riscos psicossociais',
        'Dashboards de acompanhamento',
        'Plano de ação estruturado',
        'Documentação para auditoria',
        'Suporte técnico especializado',
      ],
      image: 'https://images.unsplash.com/photo-1646227655718-dd721b681d91?w=800&h=500&fit=crop',
      path: '/solucoes/nr01',
      color: 'green',
    },
  ];

  const faqs = [
    {
      question: 'Posso contratar apenas uma das soluções?',
      answer: 'Sim! Cada solução funciona de forma independente. Você pode começar com a plataforma EAD e adicionar produção de cursos depois, ou contratar apenas o suporte para NR-01. Recomendamos avaliar suas necessidades com nossos especialistas para encontrar a melhor combinação.'
    },
    {
      question: 'Existe integração entre as soluções?',
      answer: 'Sim! Todas as nossas soluções são integradas. Os cursos produzidos pelo nosso estúdio podem ser publicados diretamente na plataforma EAD, e os treinamentos de NR-01 também ficam disponíveis na mesma interface.'
    },
    {
      question: 'Qual o prazo médio de implementação?',
      answer: 'A plataforma EAD básica pode estar operacional em 2-4 semanas. Produção de cursos varia conforme a complexidade do projeto. O módulo NR-01 pode ser ativado em poucos dias. Projetos mais complexos são avaliados individualmente.'
    },
    {
      question: 'Vocês oferecem suporte técnico?',
      answer: 'Sim! Oferecemos suporte técnico completo via chat, e-mail e telefone. Também disponibilizamos treinamento para administradores da plataforma e materiais de ajuda para os usuários finais.'
    },
  ];

  const stats = [
    { value: '500+', label: 'Empresas Atendidas', icon: Users },
    { value: '50k+', label: 'Colaboradores Treinados', icon: GraduationCap },
    { value: '1000+', label: 'Cursos Produzidos', icon: Film },
    { value: '98%', label: 'Satisfação dos Clientes', icon: Award },
  ];

  return (
    <div data-testid="solutions-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
              Soluções Completas
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Transforme a educação{' '}
              <span className="text-blue-600">corporativa</span>{' '}
              da sua empresa
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Plataforma EAD, produção de cursos e conformidade com NR-01. 
              Tudo integrado para maximizar os resultados do seu programa de treinamento.
            </p>
            <Button
              onClick={onDemoClick}
              data-testid="solutions-hero-demo-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold shadow-xl shadow-orange-500/25"
            >
              Solicitar Demonstração
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 mb-4">
                  <stat.icon className="h-6 w-6 text-blue-600" />
                </div>
                <p className="text-3xl md:text-4xl font-bold text-slate-900 mb-1">{stat.value}</p>
                <p className="text-slate-600 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Escolha a solução <span className="text-orange-500">ideal</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Cada solução foi desenvolvida para atender necessidades específicas, 
              mas funcionam ainda melhor quando integradas.
            </p>
          </motion.div>

          <div className="space-y-16">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden border-0 shadow-xl bg-white">
                  <div className={`grid lg:grid-cols-2 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                    <div className={`relative h-64 lg:h-auto ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <img
                        src={solution.image}
                        alt={solution.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/20 to-transparent" />
                    </div>
                    <CardContent className={`p-8 lg:p-12 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl mb-6 ${
                        solution.color === 'blue' ? 'bg-blue-100' :
                        solution.color === 'orange' ? 'bg-orange-100' : 'bg-green-100'
                      }`}>
                        <solution.icon className={`h-7 w-7 ${
                          solution.color === 'blue' ? 'text-blue-600' :
                          solution.color === 'orange' ? 'text-orange-600' : 'text-green-600'
                        }`} />
                      </div>
                      <span className="text-sm font-medium text-slate-500 block mb-1">
                        {solution.subtitle}
                      </span>
                      <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                        {solution.title}
                      </h3>
                      <p className="text-slate-600 mb-6">{solution.description}</p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        {solution.features.map((feature, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <Link to={solution.path}>
                        <Button
                          data-testid={`solution-btn-${index}`}
                          className={`${
                            solution.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                            solution.color === 'orange' ? 'bg-orange-500 hover:bg-orange-600' : 
                            'bg-green-600 hover:bg-green-700'
                          } text-white px-6 py-3 rounded-lg font-medium`}
                        >
                          Conhecer {solution.title}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Dúvidas <span className="text-blue-600">Frequentes</span>
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl border-0 px-6"
                data-testid={`solutions-faq-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Não sabe qual solução escolher?
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Nossos especialistas podem ajudar a identificar a melhor combinação para sua empresa.
            </p>
            <Button
              onClick={onDemoClick}
              data-testid="solutions-cta-demo-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold"
            >
              Falar com Especialista
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
